import React, { useState, useEffect, useCallback } from 'react';
import { useRegistration } from '../../../../hooks/useRegistration';
import { useNotification } from '../../../../context/NotificationContext';
import styles from './RegistrationSetup.module.css';

/**
 * RegistrationSetup - Phase 3A
 * 
 * Unified registration configuration component with:
 * - Registration toggle (enable/disable)
 * - Template selection
 * - Registration period settings
 * - Capacity settings
 * - Approval workflow
 */
const RegistrationSetup = ({ 
  programId, 
  clubId, 
  onOpenTemplateManager,
  onOpenTemplateBuilder,
  refreshTrigger = 0 // Increment this to trigger a refresh from parent
}) => {
  const { showSuccess, showError } = useNotification();
  
  // Use the existing registration hook
  const {
    config,
    loading,
    error,
    isRegistrationEnabled,
    toggleRegistration,
    updateConfig,
    getTemplates,
    refresh
  } = useRegistration(programId);

  // Local state for form
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formState, setFormState] = useState({
    registrationPeriod: {
      startDate: '',
      endDate: '',
      earlyBirdDeadline: '',
      lateRegistrationAllowed: false
    },
    capacity: {
      maxRegistrations: '',
      minParticipants: '',
      waitlistEnabled: false,
      waitlistLimit: '',
      autoEnrollFromWaitlist: true
    },
    approvalWorkflow: {
      required: true,
      autoApprove: false,
      autoApproveConditions: {
        membershipRequired: false,
        paymentRequired: false,
        documentsRequired: false
      }
    }
  });

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      const data = await getTemplates({ limit: 50 });
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  }, [getTemplates]);

  // Refresh when parent triggers it (e.g., after template selection from TemplateManager)
  useEffect(() => {
    if (refreshTrigger > 0) {
      refresh();
      loadTemplates();
    }
  }, [refreshTrigger, refresh, loadTemplates]);

  // Initialize form from config
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Update selected template when config changes
  useEffect(() => {
    if (config) {
      // Find selected template
      if (config.templateId) {
        const templateId = typeof config.templateId === 'object' 
          ? config.templateId._id 
          : config.templateId;
        
        if (typeof config.templateId === 'object') {
          setSelectedTemplate(config.templateId);
        } else {
          // Try to find in templates array
          const found = templates.find(t => t._id === templateId);
          if (found) {
            setSelectedTemplate(found);
          } else if (templates.length === 0) {
            // Templates not loaded yet, reload them
            loadTemplates();
          }
        }
      } else {
        setSelectedTemplate(null);
      }

      // Set form state from config
      setFormState({
        registrationPeriod: {
          startDate: config.registrationPeriod?.startDate 
            ? new Date(config.registrationPeriod.startDate).toISOString().split('T')[0] 
            : '',
          endDate: config.registrationPeriod?.endDate 
            ? new Date(config.registrationPeriod.endDate).toISOString().split('T')[0] 
            : '',
          earlyBirdDeadline: config.registrationPeriod?.earlyBirdDeadline 
            ? new Date(config.registrationPeriod.earlyBirdDeadline).toISOString().split('T')[0] 
            : '',
          lateRegistrationAllowed: config.registrationPeriod?.lateRegistrationAllowed || false
        },
        capacity: {
          maxRegistrations: config.capacity?.maxRegistrations || '',
          minParticipants: config.capacity?.minParticipants || '',
          waitlistEnabled: config.capacity?.waitlistEnabled || false,
          waitlistLimit: config.capacity?.waitlistLimit || '',
          autoEnrollFromWaitlist: config.capacity?.autoEnrollFromWaitlist !== false
        },
        approvalWorkflow: {
          required: config.approvalWorkflow?.required !== false,
          autoApprove: config.capacity?.autoApprove || false,
          autoApproveConditions: {
            membershipRequired: config.approvalWorkflow?.autoApproveConditions?.membershipRequired || false,
            paymentRequired: config.approvalWorkflow?.autoApproveConditions?.paymentRequired || false,
            documentsRequired: config.approvalWorkflow?.autoApproveConditions?.documentsRequired || false
          }
        }
      });
      setHasChanges(false);
    }
  }, [config, templates, loadTemplates]);

  // Handle toggle registration
  const handleToggleRegistration = async () => {
    try {
      await toggleRegistration(!isRegistrationEnabled, templates);
      showSuccess(isRegistrationEnabled 
        ? 'Registration disabled' 
        : 'Registration enabled'
      );
    } catch (err) {
      showError(err.message || 'Failed to toggle registration');
    }
  };

  // Handle template selection
  const handleTemplateSelect = async (templateId) => {
    const template = templates.find(t => t._id === templateId);
    if (!template) return;

    setSelectedTemplate(template);
    
    // Auto-save template selection
    try {
      await updateConfig({
        ...buildConfigPayload(),
        templateId: template._id
      });
      showSuccess(`Template "${template.name}" selected`);
    } catch (err) {
      showError('Failed to save template selection');
    }
  };

  // Build config payload for API
  const buildConfigPayload = () => ({
    templateId: selectedTemplate?._id,
    registrationPeriod: {
      startDate: formState.registrationPeriod.startDate 
        ? new Date(formState.registrationPeriod.startDate).toISOString() 
        : new Date().toISOString(),
      endDate: formState.registrationPeriod.endDate 
        ? new Date(formState.registrationPeriod.endDate).toISOString() 
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      earlyBirdDeadline: formState.registrationPeriod.earlyBirdDeadline 
        ? new Date(formState.registrationPeriod.earlyBirdDeadline).toISOString() 
        : undefined,
      lateRegistrationAllowed: formState.registrationPeriod.lateRegistrationAllowed
    },
    capacity: {
      maxRegistrations: formState.capacity.maxRegistrations 
        ? parseInt(formState.capacity.maxRegistrations) 
        : null,
      minParticipants: formState.capacity.minParticipants 
        ? parseInt(formState.capacity.minParticipants) 
        : 1,
      waitlistEnabled: formState.capacity.waitlistEnabled,
      waitlistLimit: formState.capacity.waitlistLimit 
        ? parseInt(formState.capacity.waitlistLimit) 
        : null,
      autoEnrollFromWaitlist: formState.capacity.autoEnrollFromWaitlist,
      autoApprove: formState.approvalWorkflow.autoApprove
    },
    approvalWorkflow: {
      required: formState.approvalWorkflow.required,
      autoApproveConditions: formState.approvalWorkflow.autoApproveConditions
    },
    paymentConfig: {
      required: false,
      amount: 0,
      currency: 'USD',
      paymentMethods: ['card']
    },
    notificationConfig: {
      confirmationEmail: { enabled: true },
      statusUpdateEmails: { enabled: true }
    },
    programSettings: {
      requireMembership: false,
      ageVerification: { required: false }
    }
  });

  // Handle save
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateConfig(buildConfigPayload());
      showSuccess('Settings saved');
      setHasChanges(false);
      refresh();
    } catch (err) {
      showError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Update form field
  const updateField = (section, field, value) => {
    setFormState(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  // Update nested field (for autoApproveConditions)
  const updateNestedField = (section, subsection, field, value) => {
    setFormState(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
    setHasChanges(true);
  };

  // Get registration status display
  const getStatusDisplay = () => {
    if (!isRegistrationEnabled) {
      return { label: 'Disabled', color: 'gray', icon: '○' };
    }
    
    const now = new Date();
    const start = formState.registrationPeriod.startDate 
      ? new Date(formState.registrationPeriod.startDate) 
      : null;
    const end = formState.registrationPeriod.endDate 
      ? new Date(formState.registrationPeriod.endDate) 
      : null;

    if (start && now < start) {
      return { label: 'Scheduled', color: 'blue', icon: '◐' };
    }
    if (end && now > end) {
      return { label: 'Closed', color: 'orange', icon: '◑' };
    }
    return { label: 'Active', color: 'green', icon: '●' };
  };

  const status = getStatusDisplay();

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading registration settings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <span className={styles.errorIcon}>⚠️</span>
        <p>{error}</p>
        <button onClick={refresh} className={styles.retryBtn}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.registrationSetup}>
      {/* Header with Toggle - Only show toggle if template is selected OR registration is already enabled */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Registration Settings</h2>
          <p>Configure how users register for this program</p>
        </div>
        <div className={styles.headerRight}>
          {(selectedTemplate || isRegistrationEnabled) && (
            <>
              <div className={styles.statusBadge} data-status={status.color}>
                <span className={styles.statusDot}>{status.icon}</span>
                <span>{status.label}</span>
              </div>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={isRegistrationEnabled}
                  onChange={handleToggleRegistration}
                />
                <span className={styles.toggleSlider}></span>
                <span className={styles.toggleLabel}>
                  {isRegistrationEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </>
          )}
        </div>
      </div>

      {/* Step 1: Template Selection - Show when no template selected and registration disabled */}
      {!selectedTemplate && !isRegistrationEnabled && (
        <div className={styles.disabledMessage}>
          <div className={styles.messageIcon}>📋</div>
          <div className={styles.messageContent}>
            <h3>Step 1: Select a Registration Template</h3>
            <p>
              Before enabling registration, you need to choose a template that defines what 
              information to collect from participants.
            </p>
          </div>
          <div className={styles.templateAccess}>
            <div className={styles.templateButtons}>
              {templates.length > 0 ? (
                <>
                  <button 
                    className={styles.primaryBtn}
                    onClick={onOpenTemplateManager}
                  >
                    📋 Browse & Select Template ({templates.length})
                  </button>
                  <button 
                    className={styles.secondaryBtn}
                    onClick={onOpenTemplateBuilder}
                  >
                    ➕ Create New Template
                  </button>
                </>
              ) : (
                <button 
                  className={styles.primaryBtn}
                  onClick={onOpenTemplateBuilder}
                >
                  ➕ Create Your First Template
                </button>
              )}
            </div>
            <p className={styles.templateHint}>
              Once you select a template, you'll be able to enable registration.
            </p>
          </div>
        </div>
      )}

      {/* Step 2: Template Selected - Show option to enable */}
      {selectedTemplate && !isRegistrationEnabled && (
        <div className={styles.disabledMessage}>
          <div className={styles.messageIcon}>✅</div>
          <div className={styles.messageContent}>
            <h3>Template Selected</h3>
            <div className={styles.selectedTemplatePreview}>
              <div className={styles.templateCard}>
                <div className={styles.templateInfo}>
                  <h4>{selectedTemplate.name}</h4>
                  <p>{selectedTemplate.description || 'No description'}</p>
                  <div className={styles.templateMeta}>
                    <span className={styles.badge}>{selectedTemplate.category}</span>
                    <span className={styles.metric}>
                      📄 {selectedTemplate.sections?.length || 0} sections
                    </span>
                    <span className={styles.metric}>
                      📎 {selectedTemplate.documentRequirements?.length || 0} documents
                    </span>
                  </div>
                </div>
                <button 
                  className={styles.outlineBtn}
                  onClick={onOpenTemplateManager}
                >
                  Change Template
                </button>
              </div>
            </div>
            <p className={styles.enableHint}>
              ✨ Your template is ready! Use the toggle above to enable registration.
            </p>
          </div>
        </div>
      )}

      {/* Configuration Sections - Only show when enabled */}
      {isRegistrationEnabled && (
        <div className={styles.configSections}>
          
          {/* Section 1: Template Selection */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3>📋 Registration Template</h3>
              <p>Choose what information to collect from participants</p>
            </div>
            <div className={styles.sectionContent}>
              {selectedTemplate ? (
                <div className={styles.selectedTemplate}>
                  <div className={styles.templateInfo}>
                    <h4>{selectedTemplate.name}</h4>
                    <p>{selectedTemplate.description || 'No description'}</p>
                    <div className={styles.templateMeta}>
                      <span className={styles.badge}>{selectedTemplate.category}</span>
                      <span className={styles.metric}>
                        📄 {selectedTemplate.sections?.length || 0} sections
                      </span>
                      <span className={styles.metric}>
                        📎 {selectedTemplate.documentRequirements?.length || 0} documents
                      </span>
                    </div>
                  </div>
                  <div className={styles.templateActions}>
                    <button 
                      className={styles.outlineBtn}
                      onClick={onOpenTemplateManager}
                    >
                      Change
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.noTemplate}>
                  <div className={styles.noTemplateIcon}>📝</div>
                  <div className={styles.noTemplateText}>
                    <h4>No Template Selected</h4>
                    <p>Select or create a template to define what information to collect</p>
                  </div>
                  {templates.length > 0 && (
                    <select 
                      className={styles.quickSelect}
                      value=""
                      onChange={(e) => handleTemplateSelect(e.target.value)}
                    >
                      <option value="">Quick select...</option>
                      {templates.map(t => (
                        <option key={t._id} value={t._id}>
                          {t.name} ({t.category})
                        </option>
                      ))}
                    </select>
                  )}
                  <div className={styles.templateButtons}>
                    {templates.length > 0 && (
                      <button 
                        className={styles.secondaryBtn}
                        onClick={onOpenTemplateManager}
                      >
                        Browse Templates
                      </button>
                    )}
                    <button 
                      className={styles.primaryBtn}
                      onClick={onOpenTemplateBuilder}
                    >
                      Create Template
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Section 2: Registration Period */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3>📅 Registration Period</h3>
              <p>Set when registration opens and closes</p>
            </div>
            <div className={styles.sectionContent}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={formState.registrationPeriod.startDate}
                    onChange={(e) => updateField('registrationPeriod', 'startDate', e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>End Date</label>
                  <input
                    type="date"
                    value={formState.registrationPeriod.endDate}
                    onChange={(e) => updateField('registrationPeriod', 'endDate', e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Early Bird Deadline <span className={styles.optional}>(optional)</span></label>
                  <input
                    type="date"
                    value={formState.registrationPeriod.earlyBirdDeadline}
                    onChange={(e) => updateField('registrationPeriod', 'earlyBirdDeadline', e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formState.registrationPeriod.lateRegistrationAllowed}
                      onChange={(e) => updateField('registrationPeriod', 'lateRegistrationAllowed', e.target.checked)}
                    />
                    <span>Allow late registration</span>
                  </label>
                  <span className={styles.hint}>Accept registrations after end date</span>
                </div>
              </div>
              
              {/* Timeline Visual */}
              {formState.registrationPeriod.startDate && formState.registrationPeriod.endDate && (
                <div className={styles.timeline}>
                  <div className={styles.timelineBar}>
                    {formState.registrationPeriod.earlyBirdDeadline && (
                      <div 
                        className={styles.earlyBirdMarker}
                        title="Early Bird Deadline"
                      >
                        🐦
                      </div>
                    )}
                  </div>
                  <div className={styles.timelineLabels}>
                    <span>
                      {new Date(formState.registrationPeriod.startDate).toLocaleDateString()}
                    </span>
                    <span>
                      {new Date(formState.registrationPeriod.endDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Section 3: Capacity Settings */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3>👥 Capacity Settings</h3>
              <p>Control how many participants can register</p>
            </div>
            <div className={styles.sectionContent}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Maximum Registrations</label>
                  <input
                    type="number"
                    placeholder="Unlimited"
                    value={formState.capacity.maxRegistrations}
                    onChange={(e) => updateField('capacity', 'maxRegistrations', e.target.value)}
                    min="1"
                  />
                  <span className={styles.hint}>Leave empty for unlimited</span>
                </div>
                <div className={styles.formGroup}>
                  <label>Minimum Participants</label>
                  <input
                    type="number"
                    placeholder="1"
                    value={formState.capacity.minParticipants}
                    onChange={(e) => updateField('capacity', 'minParticipants', e.target.value)}
                    min="1"
                  />
                  <span className={styles.hint}>Minimum needed to run</span>
                </div>
              </div>
              
              <div className={styles.toggleGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formState.capacity.waitlistEnabled}
                    onChange={(e) => updateField('capacity', 'waitlistEnabled', e.target.checked)}
                  />
                  <span>Enable waitlist when full</span>
                </label>
              </div>

              {formState.capacity.waitlistEnabled && (
                <div className={styles.subSettings}>
                  <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                      <label>Waitlist Limit</label>
                      <input
                        type="number"
                        placeholder="Unlimited"
                        value={formState.capacity.waitlistLimit}
                        onChange={(e) => updateField('capacity', 'waitlistLimit', e.target.value)}
                        min="1"
                      />
                    </div>
                  </div>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formState.capacity.autoEnrollFromWaitlist}
                      onChange={(e) => updateField('capacity', 'autoEnrollFromWaitlist', e.target.checked)}
                    />
                    <span>Auto-enroll from waitlist when spots open</span>
                  </label>
                </div>
              )}
            </div>
          </section>

          {/* Section 4: Approval Workflow */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3>✅ Approval Workflow</h3>
              <p>Configure how applications are processed</p>
            </div>
            <div className={styles.sectionContent}>
              <div className={styles.workflowOptions}>
                <label className={`${styles.workflowOption} ${formState.approvalWorkflow.autoApprove ? styles.selected : ''}`}>
                  <input
                    type="radio"
                    name="workflow"
                    checked={formState.approvalWorkflow.autoApprove}
                    onChange={() => {
                      updateField('approvalWorkflow', 'autoApprove', true);
                      updateField('approvalWorkflow', 'required', false);
                    }}
                  />
                  <div className={styles.optionContent}>
                    <span className={styles.optionIcon}>⚡</span>
                    <div>
                      <h4>Auto-Approve All</h4>
                      <p>Applications are approved immediately</p>
                    </div>
                  </div>
                </label>

                <label className={`${styles.workflowOption} ${!formState.approvalWorkflow.autoApprove ? styles.selected : ''}`}>
                  <input
                    type="radio"
                    name="workflow"
                    checked={!formState.approvalWorkflow.autoApprove}
                    onChange={() => {
                      updateField('approvalWorkflow', 'autoApprove', false);
                      updateField('approvalWorkflow', 'required', true);
                    }}
                  />
                  <div className={styles.optionContent}>
                    <span className={styles.optionIcon}>👤</span>
                    <div>
                      <h4>Manual Review</h4>
                      <p>Applications require approval</p>
                    </div>
                  </div>
                </label>
              </div>

              {!formState.approvalWorkflow.autoApprove && (
                <div className={styles.autoApproveConditions}>
                  <h4>Auto-approve if:</h4>
                  <p className={styles.conditionsHint}>
                    Applications meeting these conditions will be auto-approved
                  </p>
                  <div className={styles.conditionsList}>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={formState.approvalWorkflow.autoApproveConditions.membershipRequired}
                        onChange={(e) => updateNestedField('approvalWorkflow', 'autoApproveConditions', 'membershipRequired', e.target.checked)}
                      />
                      <span>User is a club member</span>
                    </label>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={formState.approvalWorkflow.autoApproveConditions.paymentRequired}
                        onChange={(e) => updateNestedField('approvalWorkflow', 'autoApproveConditions', 'paymentRequired', e.target.checked)}
                      />
                      <span>Payment completed</span>
                    </label>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={formState.approvalWorkflow.autoApproveConditions.documentsRequired}
                        onChange={(e) => updateNestedField('approvalWorkflow', 'autoApproveConditions', 'documentsRequired', e.target.checked)}
                      />
                      <span>All documents verified</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Workflow Diagram */}
              <div className={styles.workflowDiagram}>
                <div className={styles.workflowStep}>
                  <span className={styles.stepIcon}>📝</span>
                  <span>Submit</span>
                </div>
                <div className={styles.workflowArrow}>→</div>
                {!formState.approvalWorkflow.autoApprove && (
                  <>
                    <div className={styles.workflowStep}>
                      <span className={styles.stepIcon}>👀</span>
                      <span>Review</span>
                    </div>
                    <div className={styles.workflowArrow}>→</div>
                  </>
                )}
                <div className={styles.workflowStep}>
                  <span className={styles.stepIcon}>✅</span>
                  <span>Approved</span>
                </div>
              </div>
            </div>
          </section>

          {/* Save Actions - Only show when there are changes */}
          {hasChanges && (
            <div className={styles.saveSection}>
              <div className={styles.unsavedNotice}>
                <span>⚠️ You have unsaved changes</span>
              </div>
              <button 
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className={styles.savingSpinner}></span>
                    Saving...
                  </>
                ) : (
                  <>💾 Save Changes</>
                )}
              </button>
              <button
                className={styles.discardBtn}
                onClick={() => {
                  refresh();
                  loadTemplates();
                  setHasChanges(false);
                }}
                disabled={saving}
              >
                Discard
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RegistrationSetup;
